import { useQuery } from "@tanstack/react-query";

import { UserSearch } from "@web-app/types";

import {
  getDriverRoute,
  getDriversByIds,
  getDriversLocations,
  searchDrivers,
} from "../services/drivers";

import { useOrders } from "./useOrders";

export const useSearchDrivers = (search: string) => {
  return useQuery({
    queryFn: async ({ signal }) => {
      return await searchDrivers({ searchTerm: search, signal });
    },
    queryKey: ["drivers", "search", search],
    refetchOnWindowFocus: false,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  });
};

export const useOnlineDrivers = () => {
  return useQuery({
    queryFn: async ({ signal }) => {
      return await searchDrivers({ searchTerm: "", signal, status: "ONLINE" });
    },
    queryKey: ["drivers", "online"],
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000, // 30 seconds
  });
};
export const useDriverLocations = () => {
  const drivers = new Map<string, UserSearch>();

  const { data: onlineDrivers } = useOnlineDrivers();

  const { data } = useOrders();
  const orderAssignees = data?.assignees;

  if (onlineDrivers?.length) {
    onlineDrivers.map((driver) => drivers.set(driver.id, driver));
  }

  if (orderAssignees) {
    orderAssignees.forEach((driver) => {
      if (driver?.id) {
        drivers.set(driver.id, {
          ...driver,
          driverProfile: driver?.driverDetails,
        });
      }
    });
  }

  return useQuery({
    enabled: drivers.size > 0,
    queryFn: async () => {
      const driverLocations = await getDriversLocations(
        Array.from(drivers.keys()),
      );
      return {
        driverLocations,
        drivers,
      };
    },
    queryKey: ["driversLocations", ...drivers.keys()],
    refetchInterval: 10000,
    refetchOnWindowFocus: false,
    staleTime: 10000,
  });
};

export const useGetDrivers = (driverIds: string[]) => {
  return useQuery({
    enabled: driverIds.length > 0,
    gcTime: 24 * 60 * 60 * 1000, // 24 hours - keep in cache even when no subscribers
    queryFn: async () => {
      if (driverIds.length === 0) {
        return [];
      }
      return await getDriversByIds(driverIds);
    },
    queryKey: ["drivers", "byIds", driverIds.sort().join(",")],
    refetchOnWindowFocus: false,
    staleTime: 60 * 60 * 1000, // 1 hour - driver data doesn't change frequently
  });
};

export const useDriverRoute = (orderId: string) => {
  return useQuery({
    enabled: true,
    queryFn: getDriverRoute,
    queryKey: ["driverRoute", orderId],
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};
